/**
 * LessonAuthoringForm — Story 4.1a: Lesson authoring form with taxonomy governance.
 *
 * Enforces AC1, AC2:
 * - Condition tags: vocabulary-bound multi-select (checkboxes from live taxonomy)
 * - Derived track(s): auto-computed from selected conditions; READ-ONLY display
 * - OAP measures: vocabulary-bound multi-select with save-gate hint when 0 selected
 * - Phase: optional numeric learning-priority input
 * - CMS measurement window and touch level: NEVER shown (computed member-side)
 *
 * @NonPhiEndpoint — lesson content is clinical corpus, not member PHI.
 */

import type { JSX } from 'react';
import { useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface TaxonomyConditionOption {
  conditionCode: string;
  canonicalName: string;
  track: string;
  supportedOapMeasures: string[];
  status: 'active' | 'retired';
}

export interface LessonAuthoringFormProps {
  /** Active condition taxonomy entries from the live taxonomy API */
  taxonomy: TaxonomyConditionOption[];
  /** Available OAP measure codes from the platform vocabulary */
  oapMeasures: string[];
  /** Callback when the author saves the lesson form */
  onSave: (data: LessonFormData) => void;
  /** Optional initial values (for editing existing lessons) */
  initialValues?: Partial<LessonFormData>;
}

export interface LessonFormData {
  title: string;
  clinicalMessage: string;
  conditionTags: string[];
  /** Derived from conditionTags — never author-supplied */
  derivedTracks: string[];
  oapMeasureBindings: string[];
  phase: number | null;
  contentType: string;
  format: string;
  languageCode: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Derive the de-duplicated track set from selected condition codes.
 * Mirrors the domain deriveTracks() function — pure, no I/O.
 */
function deriveTracks(selectedCodes: string[], taxonomy: TaxonomyConditionOption[]): string[] {
  const tracks = new Set<string>();
  for (const code of selectedCodes) {
    const entry = taxonomy.find((e) => e.conditionCode === code);
    if (entry) tracks.add(entry.track);
  }
  return [...tracks];
}

// ── Component ──────────────────────────────────────────────────────────────────

export function LessonAuthoringForm({
  taxonomy,
  oapMeasures,
  onSave,
  initialValues,
}: LessonAuthoringFormProps): JSX.Element {
  const activeTaxonomy = taxonomy.filter((e) => e.status === 'active');

  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [clinicalMessage, setClinicalMessage] = useState(initialValues?.clinicalMessage ?? '');
  const [conditionTags, setConditionTags] = useState<string[]>(initialValues?.conditionTags ?? []);
  const [oapMeasureBindings, setOapMeasureBindings] = useState<string[]>(
    initialValues?.oapMeasureBindings ?? [],
  );
  const [phase, setPhase] = useState<number | null>(initialValues?.phase ?? null);
  const [contentType, setContentType] = useState(initialValues?.contentType ?? 'lesson');
  const [format, setFormat] = useState(initialValues?.format ?? 'read');
  const [languageCode, setLanguageCode] = useState(initialValues?.languageCode ?? 'en');

  // Derived tracks are auto-computed from selected condition codes
  const derivedTracks = deriveTracks(conditionTags, activeTaxonomy);

  const hasOapMeasure = oapMeasureBindings.length > 0;

  const toggleCondition = (code: string): void => {
    setConditionTags((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleOapMeasure = (measure: string): void => {
    setOapMeasureBindings((prev) =>
      prev.includes(measure) ? prev.filter((m) => m !== measure) : [...prev, measure],
    );
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    onSave({
      title,
      clinicalMessage,
      conditionTags,
      derivedTracks,
      oapMeasureBindings,
      phase,
      contentType,
      format,
      languageCode,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div>
        <label htmlFor="lesson-title" className="block text-sm font-medium text-gray-700">
          Title
        </label>
        <input
          id="lesson-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          required
        />
      </div>

      {/* Clinical Message */}
      <div>
        <label htmlFor="clinical-message" className="block text-sm font-medium text-gray-700">
          Clinical Message
          <span className="ml-1 text-xs text-gray-500">(one canonical message per lesson)</span>
        </label>
        <textarea
          id="clinical-message"
          value={clinicalMessage}
          onChange={(e) => setClinicalMessage(e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          required
        />
      </div>

      {/* Content Type */}
      <div>
        <label htmlFor="content-type" className="block text-sm font-medium text-gray-700">
          Content Type
        </label>
        <select
          id="content-type"
          value={contentType}
          onChange={(e) => setContentType(e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="lesson">Lesson</option>
          <option value="quiz">Quiz</option>
          <option value="recipe-card">Recipe Card</option>
          <option value="shopping-guide">Shopping Guide</option>
        </select>
      </div>

      {/* Format */}
      <div>
        <label htmlFor="format" className="block text-sm font-medium text-gray-700">
          Format
        </label>
        <select
          id="format"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="read">Read</option>
          <option value="watch">Watch</option>
          <option value="listen">Listen</option>
        </select>
      </div>

      {/* Language */}
      <div>
        <label htmlFor="language-code" className="block text-sm font-medium text-gray-700">
          Language
        </label>
        <select
          id="language-code"
          value={languageCode}
          onChange={(e) => setLanguageCode(e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="en">English</option>
          <option value="es">Spanish</option>
        </select>
      </div>

      {/* ── Condition Tags (vocabulary-bound) ──────────────────────────────────── */}
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700">
          Condition Tags
          <span className="ml-1 text-xs text-gray-500">
            (controlled vocabulary — select all that apply)
          </span>
        </legend>
        <div className="mt-2 space-y-2">
          {activeTaxonomy.map((entry) => (
            <label key={entry.conditionCode} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label={entry.canonicalName}
                checked={conditionTags.includes(entry.conditionCode)}
                onChange={() => toggleCondition(entry.conditionCode)}
                className="rounded border-gray-300"
              />
              <span className="font-medium">{entry.conditionCode}</span>
              <span className="text-gray-600">{entry.canonicalName}</span>
              <span className="ml-auto text-xs text-gray-400">{entry.track}</span>
            </label>
          ))}
          {activeTaxonomy.length === 0 && (
            <p className="text-sm text-gray-500">No active conditions in taxonomy</p>
          )}
        </div>
      </fieldset>

      {/* ── Derived Tracks (read-only) ─────────────────────────────────────────── */}
      <div>
        <span className="block text-sm font-medium text-gray-700">
          Derived Tracks
          <span className="ml-1 text-xs text-gray-500">
            (auto-derived from conditions — read only)
          </span>
        </span>
        <div
          data-testid="derived-tracks-display"
          className="mt-1 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700"
          aria-label="Derived clinical tracks (read only)"
        >
          {derivedTracks.length > 0 ? (
            derivedTracks.join(', ')
          ) : (
            <span className="text-gray-400 italic">Select conditions above to derive tracks</span>
          )}
        </div>
      </div>

      {/* ── OAP Measure Bindings ─────────────────────────────────────────────── */}
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700">
          OAP Measure Bindings
          <span className="ml-1 text-xs text-gray-500">(at least one required)</span>
        </legend>
        <div className="mt-2 space-y-2">
          {oapMeasures.map((measure) => (
            <label key={measure} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label={measure}
                checked={oapMeasureBindings.includes(measure)}
                onChange={() => toggleOapMeasure(measure)}
                className="rounded border-gray-300"
              />
              <span className="font-mono text-sm">{measure}</span>
            </label>
          ))}
        </div>
        {!hasOapMeasure && (
          <p data-testid="oap-save-gate-hint" className="mt-2 text-xs text-amber-700">
            Bind at least one OAP measure to enable saving.
          </p>
        )}
      </fieldset>

      {/* ── Phase (advisory numeric priority) ────────────────────────────────── */}
      <div>
        <label htmlFor="phase" className="block text-sm font-medium text-gray-700">
          Phase
          <span className="ml-1 text-xs text-gray-500">
            (advisory learning priority — optional)
          </span>
        </label>
        <input
          id="phase"
          type="number"
          value={phase ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            setPhase(val === '' ? null : Number(val));
          }}
          min={1}
          placeholder="e.g. 1, 2, 3…"
          className="mt-1 block w-48 rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {/* NOTE: CMS measurement window and touch level are intentionally NOT
          rendered here. They are computed member-side and must never appear
          as content-authoring fields (AC2). */}

      {/* Submit */}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={!hasOapMeasure}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Save Lesson
        </button>
      </div>
    </form>
  );
}
